import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import fetchWithAuth from "../../../../util/fetchWithAuth";
import { API_URL } from "../../../../util/url";
import StyledTable from "../../../../shared/StyledTable/StyledTable";
import { cloneTemplate, getTemplates } from "../../../../api/scriptsApi";

const Accordion = styled((props) => (
  <MuiAccordion
    className="rmui-card"
    disableGutters
    elevation={0}
    square
    {...props}
  />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon
        sx={{ fontSize: "0.9rem", color: "text.secondary" }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row",
  "& .MuiAccordionSummary-content": {
    marginRight: theme.spacing(1),
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    marginLeft: "auto",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: 0,
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const kindLabel = (kind) => {
  if (!kind) return "—";
  if (kind === "client_card") return "Картка клієнта";
  if (kind === "sale") return "Продаж";
  return kind;
};

const TemplatesBlock = ({ editorPath = "/supervisor/script-templates/edit" }) => {
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [crmOptions, setCrmOptions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createForm, setCreateForm] = useState({
    crmId: "",
    sourceTemplateId: "",
    title: "",
  });
  const navigate = useNavigate();

  const handleChange = (panel) => (_, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [crmResponse, templatesResponse] = await Promise.all([
          fetchWithAuth(API_URL + "/statistic/crm/"),
          getTemplates(),
        ]);

        if (!crmResponse.ok) {
          throw new Error("Не вдалося завантажити CRM");
        }
        const crmJson = await crmResponse.json();
        const crmList = Array.isArray(crmJson)
          ? crmJson
          : crmJson?.results || [];

        if (!isActive) return;
        const templatesList = Array.isArray(templatesResponse)
          ? templatesResponse
          : templatesResponse?.results || [];
        setCrmOptions(Array.isArray(crmList) ? crmList : []);
        setTemplates(Array.isArray(templatesList) ? templatesList : []);
        const defaultSourceTemplate =
          templatesList.find(
            (template) => template.product_id === null || template.product_id === undefined
          ) || templatesList[0];
        setCreateForm((prev) => ({
          ...prev,
          crmId:
            prev.crmId && crmList.some((crm) => String(crm.id) === prev.crmId)
              ? prev.crmId
              : String(crmList[0]?.id || ""),
          sourceTemplateId:
            prev.sourceTemplateId &&
            templatesList.some((template) => String(template.id) === prev.sourceTemplateId)
              ? prev.sourceTemplateId
              : String(defaultSourceTemplate?.id || ""),
        }));
      } catch (err) {
        if (!isActive) return;
        setError(err instanceof Error ? err : new Error("Помилка запиту"));
      } finally {
        if (isActive) setLoading(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, []);

  const crmNameById = useMemo(() => {
    const map = {};
    (crmOptions || []).forEach((crm) => {
      if (crm?.id == null) return;
      map[String(crm.id)] = crm.name || `CRM #${crm.id}`;
    });
    return map;
  }, [crmOptions]);

  const templatesByCrm = useMemo(() => {
    const map = new Map();
    templates.forEach((template) => {
      const key =
        template?.crm !== undefined && template?.crm !== null
          ? String(template.crm)
          : "none";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(template);
    });
    return map;
  }, [templates]);

  const crmKeys = useMemo(() => {
    const keys = new Set();
    templates.forEach((template) => {
      if (template?.crm !== undefined && template?.crm !== null) {
        keys.add(String(template.crm));
      }
    });
    return Array.from(keys);
  }, [templates]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card className="rmui-card" sx={{ p: 2, mt: 3 }}>
        <Typography color="error">
          Не вдалося завантажити шаблони.
        </Typography>
      </Card>
    );
  }

  const crmPanels = crmKeys.map((crmKey, index) => {
    const templatesForCrm = templatesByCrm.get(crmKey) || [];
    if (!templatesForCrm.length) return null;
    const panelKey = `templates-panel-${crmKey ?? index}`;
    const fallbackTitle = templatesForCrm[0]?.title || "";
    const normalizedTitle = fallbackTitle
      .replace(/^шаблон\s+/i, "")
      .replace(/^template\s+/i, "")
      .trim();
    const crmLabel =
      crmNameById[crmKey] || normalizedTitle || `CRM #${crmKey}`;

    return (
      <Accordion
        key={panelKey}
        expanded={expanded === panelKey}
        onChange={handleChange(panelKey)}
        sx={{ mb: 1 }}
        className="rmui-card"
      >
        <AccordionSummary sx={{ padding: "10px 25px" }}>
          <Typography
            variant="h6"
            fontWeight={600}
            fontSize="15px"
            flexGrow={1}
          >
            {crmLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StyledTable
            headers={["Назва", "Тип", "ID", "Дія"]}
            rows={templatesForCrm}
            renderRow={(template) => (
              <TableRow key={template.id}>
                <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                  <Typography variant="subtitle2" fontWeight={500}>
                    {template.title || `Template #${template.id}`}
                  </Typography>
                </TableCell>
                <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                  {kindLabel(template.kind)}
                </TableCell>
                <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                  {template.id}
                </TableCell>
                <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        `${editorPath}?template_id=${template.id}&template_source=templates`
                      )
                    }
                  >
                    Редагувати
                  </Button>
                </TableCell>
              </TableRow>
            )}
          />
        </AccordionDetails>
      </Accordion>
    );
  });

  const templatesWithoutCrm = templatesByCrm.get("none") || [];

  const getCreateError = (err) => {
    try {
      const payload = JSON.parse(err?.message || "");
      return payload?.detail || "Не вдалося створити шаблон.";
    } catch {
      return err?.message || "Не вдалося створити шаблон.";
    }
  };

  const handleCreateTemplate = async () => {
    if (!createForm.crmId || !createForm.sourceTemplateId) {
      setCreateError("Оберіть CRM і базовий шаблон.");
      return;
    }

    setCreateSaving(true);
    setCreateError("");
    try {
      const created = await cloneTemplate({
        crm: Number(createForm.crmId),
        source_template_id: Number(createForm.sourceTemplateId),
        ...(createForm.title.trim() ? { title: createForm.title.trim() } : {}),
      });
      setTemplates((prev) => [created, ...prev]);
      setCreateOpen(false);
      setCreateForm((prev) => ({ ...prev, title: "" }));
      navigate(
        `${editorPath}?template_id=${created.id}&template_source=templates`
      );
    } catch (err) {
      setCreateError(getCreateError(err));
    } finally {
      setCreateSaving(false);
    }
  };

  return (
    <Box mt={4}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Шаблони
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setCreateError("");
            setCreateOpen(true);
          }}
        >
          Створити базовий шаблон
        </Button>
      </Box>
      <Card className="rmui-card" sx={{ p: 2 }}>
        {crmPanels.some(Boolean) ? crmPanels : (
          <Typography color="text.secondary">Немає шаблонів для CRM.</Typography>
        )}
        {templatesWithoutCrm.length > 0 && (
          <Accordion
            expanded={expanded === "templates-panel-none"}
            onChange={handleChange("templates-panel-none")}
            sx={{ mb: 1, mt: 2 }}
            className="rmui-card"
          >
            <AccordionSummary sx={{ padding: "10px 25px" }}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="15px"
                flexGrow={1}
              >
                Без CRM
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StyledTable
                headers={["Назва", "Тип", "ID", "Дія"]}
                rows={templatesWithoutCrm}
                renderRow={(template) => (
                  <TableRow key={template.id}>
                    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {template.title || `Template #${template.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                      {kindLabel(template.kind)}
                    </TableCell>
                    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                      {template.id}
                    </TableCell>
                    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `${editorPath}?template_id=${template.id}&template_source=templates`
                          )
                        }
                      >
                        Редагувати
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </Card>

      <Dialog
        open={createOpen}
        onClose={() => !createSaving && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Створити базовий шаблон CRM</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth required>
              <InputLabel>CRM</InputLabel>
              <Select
                value={createForm.crmId}
                label="CRM"
                onChange={(event) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    crmId: event.target.value,
                  }))
                }
              >
                {crmOptions.map((crm) => (
                  <MenuItem key={crm.id} value={String(crm.id)}>
                    {crm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Базовий шаблон</InputLabel>
              <Select
                value={createForm.sourceTemplateId}
                label="Базовий шаблон"
                onChange={(event) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    sourceTemplateId: event.target.value,
                  }))
                }
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={String(template.id)}>
                    {template.title || `Шаблон #${template.id}`} · {kindLabel(template.kind)} · {template.product_id ? `товар ${template.product_id}` : "базовий"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Назва базового шаблону"
              value={createForm.title}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, title: event.target.value }))
              }
              placeholder="Залиште порожнім для автоматичної назви"
            />
            {createError && <Alert severity="error">{createError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSaving}>
            Скасувати
          </Button>
          <Button
            onClick={handleCreateTemplate}
            variant="contained"
            disabled={createSaving || !templates.length}
          >
            {createSaving ? <CircularProgress size={22} color="inherit" /> : "Створити"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesBlock;
